// 用户行为数据追踪
import Cookies from "js-cookie";
import { isLogin } from "./loginModel";
import { getLocalItem, setLocalItem } from "@/lib/commonService";
import { getSite, getSiteId } from "@/src/utils/configUtils";
import { HandleJavaCommonFetch } from "../utils/fetch";
import { getVistorId } from "./customerShow";

// 事件类型
export type EventType =
    | "clickProduct" // 点击产品
    | "addCart" // 添加到购物车
    | "addWishlist" // 添加到收藏列表
    | "tryOn" // 尝试try-on
    | "createOrder" // 创建订单 order
    | "paidOrder"; // 已支付订单 pay

// 事件列表接口
interface EventList {
    eventType: EventType; // 事件类型
    itemId: string; // 关联的商品ID，格式为productId-colorId
    sentAt: string; // 事件发送时间
}
// 批量事件接口
interface PersonalizeBatchEvent {
    customersId: number; // 客户ID，注意这不是uid，而是登录后的customerId
    eventList: EventList[]; // 事件列表
    siteId: number; // 站点ID
}

/**
 * 自然来源，在登录后调用，补全用户行为
 * @param data PersonalizeBatchEvent
 */
export function personalizeBatchEvent(data: PersonalizeBatchEvent) {
    const params = { ...data, eventList: JSON.stringify(data.eventList) };
    return HandleJavaCommonFetch.post(`/front/personalize/batchEvent`, params);
}

// 单个事件接口
interface PersonalizePutEvent {
    userId: string; // 用户ID，这是uid
    siteId: number; // 站点ID
    eventType: EventType; // 事件类型
    itemId: string; // 关联的商品ID，格式为productId-colorId
}

/**
 * 带有uid的页面进入后调用，补全用户行为
 * @param data 见PersonalizePutEvent
 */
export function personalizePutEvent(data: PersonalizePutEvent) {
    return HandleJavaCommonFetch.post(`/front/personalize/putEvent`, data);
}

// 注册接口
interface PersonalizeRegister {
    siteId: number; // 站点ID
    userId?: string; // 用户ID
}

/**
 * 带有uid，注册后调用，补全用户行为
 * @param data PersonalizeRegister
 * @returns
 */
export function personalizeRegister(email: string) {
    const userId = getUid();
    if (!userId) return;
    const params = {
        userId: "U" + userId,
        siteId: window.getSiteId(),
        registerMail: email || getLocalItem("email"), //注册邮箱
    };
    return HandleJavaCommonFetch.post(`/front/personalize/register`, params);
}

// 是否存在uid，不存在则从url中获取
export function getUid(cookie?: Record<string, string>) {
    if (cookie) {
        return cookie["uid"];
    } else {
        const uid =
            new URLSearchParams(window.location.search).get("uid") ||
            Cookies.get("uid");
        return uid;
    }
}

// 补全用户行为的事件
export function personalizeEvent(name: EventType, id: string | string[]) {
    const uid = getUid();
    const login = isLogin();
    const site = window.getSiteId();

    // 未登录时，补全用户行为
    if (!login) {
        // 带有uid
        // 带有uid
        if (uid) {
            if (Array.isArray(id)) {
                id.forEach((item) => {
                    const params = {
                        userId: "U" + uid,
                        siteId: site,
                        eventType: name,
                        itemId: item,
                    };
                    personalizePutEvent(params);
                });
            } else {
                const params = {
                    userId: "U" + uid,
                    siteId: site,
                    eventType: name,
                    itemId: id,
                };
                personalizePutEvent(params);
            }

            return;
        } else {
            // 没有uid，存储到本地
            const batchEventList = JSON.parse(
                getLocalItem("batchEventList") || "[]",
            );
            const event = {
                eventType: name,
                itemId: id,
                sentAt: new Date().getTime(),
            };
            if (batchEventList.length >= 30) {
                batchEventList.shift(); // 移除最早的事件
            }
            batchEventList.push(event);
            setLocalItem("batchEventList", JSON.stringify(batchEventList));
            return;
        }
    }
}

// 批量补齐用户行为，登录时使用
export function personalizeBatchEventLogin(customersId: number) {
    const login = isLogin();
    const uid = getUid();
    if (!login || uid) return;
    // const batchEventList = JSON.parse(getLocalItem("batchEventList") || "[]");
    const batchEventStr = getLocalItem("batchEventList");
    // 先判断是否为字符串类型，再执行后续操作
    const batchEventList =
        typeof batchEventStr === "string" && batchEventStr.trim()
            ? (() => {
                  try {
                      return JSON.parse(batchEventStr);
                  } catch (e) {
                      console.error("解析batchEventList失败:", e);
                      return [];
                  }
              })()
            : [];
    const params = {
        customersId: customersId, // 客户ID，注意这不是uid，而是登录后的customerId
        eventList: batchEventList, // 事件列表
        siteId: window.getSiteId(), // 站点ID
    };
    personalizeBatchEvent(params).then(() => {
        // 清除本地存储
        setLocalItem("batchEventList", "[]");
    });
}

interface PersonalizePutEventData {
    eventType: EventType; // 事件类型
    itemId: string; // 关联的商品ID，格式为productId-colorId
}

class PersonalizeEvent {
    isPersonalize = false;

    async init(domain: string) {
        if (getSite(domain) === "us") {
            this.isPersonalize = false;
            return;
        }
        this.isPersonalize = await this.getPersonalizeStatus(domain);
        return this.isPersonalize;
    }
    putEvent(eventType: EventType, itemId: string) {
        if (this.isPersonalize) {
            this.personalizePutEventData({ eventType, itemId });
        }
    }

    personalizePutEventData(data: PersonalizePutEventData) {
        const uid = getUid();
        const visitorId = getVistorId();
        const userid = uid ? "U" + uid : "V" + visitorId;
        if (this.isPersonalize) {
            let sessionId;

            try {
                const sessionData = getLocalItem(
                    "aws-solution/clickstream-web/sessionKey",
                );

                if (sessionData) {
                    sessionId = JSON.parse(sessionData).sessionId;
                }
            } catch (error) {
                console.log("error", error);
            }
            const params = {
                userId: userid,
                siteId: window.getSiteId(),
                eventType: data.eventType,
                itemId: data.itemId,
                sessionId: sessionId,
            };

            return HandleJavaCommonFetch.post(
                `/front/personalize/putEventData`,
                params,
            );
        } else {
            return;
        }
    }

    async getPersonalizeStatus(domain: string): Promise<boolean> {
        const siteId = getSiteId(domain);
        if (!siteId) {
            console.error(
                "千人千面埋点没有获取到siteid",
                domain,
                window.location.href,
            );
        }
        return HandleJavaCommonFetch.get(
            `/front/personalize/personalizeStatus/${siteId}`,
        ).then((res) => {
            if (!!res) {
                return true;
            } else {
                return false;
            }
        });
    }
}

const personalizeEvents = new PersonalizeEvent();
export default personalizeEvents;
